import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useState } from 'react'

interface ExceptionTimeCardProps {
    validFrom: string
    validTo: string
    validOnDay: string[]
    onUpdate: (data: { validFrom: string; validTo: string; validOnDay: string[] }) => void
}

const weekdays = [
    { label: 'Mo', value: 'MONDAY' },
    { label: 'Di', value: 'TUESDAY' },
    { label: 'Mi', value: 'WEDNESDAY' },
    { label: 'Do', value: 'THURSDAY' },
    { label: 'Fr', value: 'FRIDAY' },
    { label: 'Sa', value: 'SATURDAY' },
    { label: 'So', value: 'SUNDAY' }
]

export function ExceptionTimeCard({ validFrom, validTo, validOnDay, onUpdate }: ExceptionTimeCardProps) {
    const [editMode, setEditMode] = useState(false)
    const [localFrom, setLocalFrom] = useState(validFrom)
    const [localTo, setLocalTo] = useState(validTo)
    const [localDays, setLocalDays] = useState<string[]>(validOnDay)

    const toggleDay = (day: string) => {
        if (!editMode) return
        setLocalDays(prev =>
            prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
        )
    }

    const handleSave = () => {
        onUpdate({
            validFrom: localFrom,
            validTo: localTo,
            validOnDay: localDays
        })
        setEditMode(false)
    }

    const handleCancel = () => {
        setLocalFrom(validFrom)
        setLocalTo(validTo)
        setLocalDays(validOnDay)
        setEditMode(false)
    }

    return (
        <div className="flex justify-between gap-4 p-4 rounded-lg bg-white border border-gray-200">
            <div className="flex-1 space-y-4">
                {/* Tagesauswahl */}
                <div className="space-y-2">
                    <Label className="text-xs text-gray-500 font-medium">Aktiv an Tagen</Label>
                    <div className="flex flex-wrap gap-2">
                        {weekdays.map(day => (
                            <Badge
                                key={day.value}
                                onClick={() => toggleDay(day.value)}
                                className={`h-8 w-8 flex items-center justify-center cursor-pointer transition-all text-xs ${localDays.includes(day.value)
                                        ? 'bg-brand-primary text-white border-brand-primary hover:bg-brand-primary/90'
                                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-brand-primary hover:text-brand-primary'
                                    }`}
                                variant="outline"
                            >
                                {day.label}
                            </Badge>
                        ))}
                    </div>
                </div>

                {/* Zeitauswahl */}
                <div className="space-y-2">
                    <Label className="text-xs text-gray-500 font-medium">Zeitraum</Label>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Von</span>
                            <Input
                                type="time"
                                value={localFrom}
                                onChange={(e) => setLocalFrom(e.target.value)}
                                disabled={!editMode}
                                className="w-32 h-9"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Bis</span>
                            <Input
                                type="time"
                                value={localTo}
                                onChange={(e) => setLocalTo(e.target.value)}
                                disabled={!editMode}
                                className="w-32 h-9"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
                {editMode ? (
                    <>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSave}
                            className="border-brand-primary text-brand-primary hover:bg-brand-primary/10"
                        >
                            Speichern
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancel}
                            className="text-gray-500"
                        >
                            Abbrechen
                        </Button>
                    </>
                ) : (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditMode(true)}
                        className="border-brand-primary text-brand-primary hover:bg-brand-primary/10"
                    >
                        Bearbeiten
                    </Button>
                )}
            </div>
        </div>
    )
}
